import { z } from 'zod';

/**
 * Per-tenant appearance: colors and font for this church's own copy of the
 * app shell. A tenant may save several themes but only one is ever
 * `isActive` — the seeded default is cloneable (like a system role) but
 * never editable or deletable in place.
 */

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;
const hexColor = () => z.string().regex(HEX_COLOR_PATTERN, 'Must be a hex color, e.g. #2F5D8F');

/** Curated so every option keeps working multi-script fallback — the
 *  chosen font is layered in front of the existing Noto stack, never
 *  replacing it, so Arabic/Urdu/Bengali/Hindi/Chinese text is unaffected. */
export const THEME_FONT_KEYS = [
  'system',
  'inter',
  'poppins',
  'roboto',
  'open-sans',
  'lato',
  'nunito',
  'work-sans',
  'source-sans',
  'merriweather',
] as const;
export type ThemeFontKey = (typeof THEME_FONT_KEYS)[number];

export const themeTokensSchema = z.object({
  headerBg: hexColor(),
  sidebarBg: hexColor(),
  footerBg: hexColor(),
  primaryColor: hexColor(),
  secondaryColor: hexColor(),
  tertiaryColor: hexColor(),
  textColor: hexColor(),
  fontFamily: z.enum(THEME_FONT_KEYS),
});
export type ThemeTokens = z.infer<typeof themeTokensSchema>;

/** The app's own current design — what every tenant starts with, and what
 *  `seedDefaultTheme` writes verbatim from `tokens.css`'s own values. */
export const DEFAULT_THEME_TOKENS: ThemeTokens = {
  headerBg: '#ffffff',
  sidebarBg: '#ffffff',
  footerBg: '#ffffff',
  primaryColor: '#2f5d8f',
  secondaryColor: '#ffffff',
  tertiaryColor: '#5a636e',
  textColor: '#14181d',
  fontFamily: 'system',
};

export const createThemeRequestSchema = z.object({
  name: z.string().trim().min(1).max(100),
  tokens: themeTokensSchema,
  /** Client-side convenience only — the server never reads this back
   *  beyond validating the id belongs to this tenant; it just means the
   *  caller already copied that theme's tokens into `tokens` above. */
  cloneFromThemeId: z.uuid().nullish(),
});
export type CreateThemeRequest = z.infer<typeof createThemeRequestSchema>;

/** Same shape as create — a theme's tokens are replaced wholesale, never
 *  patched field by field, since the editor always submits the complete set. */
export const updateThemeRequestSchema = z.object({
  name: z.string().trim().min(1).max(100),
  tokens: themeTokensSchema,
});
export type UpdateThemeRequest = z.infer<typeof updateThemeRequestSchema>;

export interface ThemeResponse {
  readonly id: string;
  readonly name: string;
  readonly isDefault: boolean;
  readonly isActive: boolean;
  readonly tokens: ThemeTokens;
  readonly createdAt: string;
  readonly updatedAt: string;
}
